import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

interface QueryUsersPayload {
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface QueryUsersResponse {
  data: any[];
  total: number;
}

const getUserApi = async (): Promise<any> => {
  const response = await httpClient.get('api/user/details', {});
  return response.data;
};

export async function fetchUserDetailsForAdmin(id: number): Promise<{
  user: any;
  address: any;
  roles: any[];
  userRoles: any[];
  businessUnitIds: number[];
}> {
  const response = await httpClient.get('api/user/details', { id });
  const data = response.data as any;
  return {
    user: data.user,
    address: data.address,
    roles: data.roles,
    userRoles: data.userRoles,
    businessUnitIds: data.businessUnitIds ?? [],
  };
}

const getUserActionApi = async (payload: any): Promise<any> => {
  const response = await httpClient.get('api/user/get-user', payload);
  return response.data;
};

const queryUsersApi = async (payload: QueryUsersPayload): Promise<QueryUsersResponse> => {
  const response = await httpClient.post<QueryUsersResponse>('api/user/query', payload);
  return response.data;
};

const createUserApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/user/create', payload);
  return response.data;
};

const updateUserApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/user/update', payload);
  return response.data;
};

const updateMeApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/user/update-me', payload);
  return response.data;
};

const passwordResetApi = async (payload: { email: string }): Promise<any> => {
  const response = await httpClient.post('api/user/request-reset-password', payload);
  return response.data;
};

const passwordSetApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/user/set-password', payload);
  return response.data;
};

export const useUser = () => {
  return useQuery({
    queryKey: ['user', 'details'],
    queryFn: getUserApi,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useGetUser = () => {
  return useMutation({
    mutationFn: getUserActionApi,
  });
};

export const useQueryUsers = () => {
  return useMutation({
    mutationFn: queryUsersApi,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserApi,
    onSuccess: (data, variables) => {
      // Update specific user in cache if we have the ID
      if (variables.id) {
        queryClient.setQueryData(['user', variables.id], data);
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useUpdateMe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMeApi,
    onSuccess: (data) => {
      // API returns { authedUser } from POST api/user/update-me — not { user }
      const nextUser = data?.authedUser ?? data?.user ?? data;
      queryClient.setQueryData(['currentUser'], nextUser);
      queryClient.invalidateQueries({ queryKey: ['user', 'details'] });
    },
  });
};

export const usePasswordReset = () => {
  return useMutation({
    mutationFn: passwordResetApi,
  });
};

export const usePasswordSet = () => {
  return useMutation({
    mutationFn: passwordSetApi,
  });
};
